import { useId } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EventParticipantDetails, Profile } from "@lezz/shared";
import { apiFetch } from "@/shared/lib/bff/client";
import { logger } from "@/shared/lib/logger";
import { useRealtimeInvalidation } from "@/shared/hooks/use-realtime-invalidation";
import { eventCouponKeys } from "@/features/events/api/event-coupon";

export type ParticipantType = NonNullable<
  NonNullable<EventParticipantDetails["participantType"]>
>;

// ---------------------------------------------------------------------------
// BFF-backed functions
// ---------------------------------------------------------------------------

export async function fetchEventParticipants(
  eventId: string
): Promise<EventParticipantDetails[]> {
  return apiFetch<EventParticipantDetails[]>(
    `/api/events/${eventId}/participants`
  );
}

export interface PublicEventParticipantsPreview {
  /** Up to 4 participants, enough for the avatar preview. */
  participants: EventParticipantDetails[];
  /** Total participant count across the whole event. */
  totalCount: number;
}

export async function fetchPublicEventParticipants(
  eventId: string
): Promise<PublicEventParticipantsPreview> {
  return apiFetch<PublicEventParticipantsPreview>(
    `/api/events/public/${eventId}/participants`
  );
}

export interface JoinEventParams {
  eventId: string;
  userId: string;
  participantType: ParticipantType;
  userProfile?: Profile | null;
}

export async function joinEvent(params: JoinEventParams): Promise<void> {
  const { eventId, participantType } = params;
  await apiFetch(`/api/events/${eventId}/join`, {
    method: "POST",
    body: JSON.stringify({ participantType }),
  });

  logger.info("Event joined", { eventId, participantType });
}

export interface LeaveEventParams {
  eventId: string;
  userId: string;
}

export async function leaveEvent(params: LeaveEventParams): Promise<void> {
  const { eventId } = params;
  await apiFetch(`/api/events/${eventId}/join`, {
    method: "DELETE",
  });

  logger.info("Event left", { eventId });
}

// ---------------------------------------------------------------------------
// BFF-backed: user events
// ---------------------------------------------------------------------------

export async function fetchUserEvents(
  userId: string
): Promise<EventParticipantDetails[]> {
  return apiFetch<EventParticipantDetails[]>(`/api/profile/${userId}/events`);
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const eventParticipantKeys = {
  all: ["eventParticipants"] as const,
  byEvent: (eventId: string) => [...eventParticipantKeys.all, eventId] as const,
  publicPreview: (eventId: string) =>
    [...eventParticipantKeys.all, "publicPreview", eventId] as const,
  byUser: (userId: string) =>
    [...eventParticipantKeys.all, "user", userId] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useEventParticipants(eventId: string) {
  const instanceId = useId();

  useRealtimeInvalidation(
    `event_participants:${eventId}:${instanceId}`,
    "event_participants",
    [eventParticipantKeys.byEvent(eventId)],
    { filter: `event_id=eq.${eventId}`, enabled: Boolean(eventId) }
  );

  return useQuery({
    queryKey: eventParticipantKeys.byEvent(eventId),
    queryFn: () => fetchEventParticipants(eventId),
    enabled: !!eventId,
    staleTime: 30_000,
  });
}

/**
 * Reads the participant preview (up to 4 profiles + total count) from the
 * public (anon) endpoint. Used by the logged-out event detail page for the
 * avatar preview. Uses its own cache key since the shape differs from the
 * authenticated `byEvent` list; the authed hook refetches on login.
 */
export function usePublicEventParticipants(eventId: string) {
  return useQuery({
    queryKey: eventParticipantKeys.publicPreview(eventId),
    queryFn: () => fetchPublicEventParticipants(eventId),
    enabled: !!eventId,
    staleTime: 30_000,
  });
}

export function useJoinEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: joinEvent,
    onMutate: async ({ eventId, userId, participantType, userProfile }) => {
      await queryClient.cancelQueries({
        queryKey: eventParticipantKeys.byEvent(eventId),
      });

      const previousParticipants = queryClient.getQueryData<
        EventParticipantDetails[]
      >(eventParticipantKeys.byEvent(eventId));

      if (userProfile) {
        const optimisticParticipant = {
          eventId,
          userId: userProfile.userId,
          participantType,
          profileId: userProfile.profileId,
          profileUsername: userProfile.username,
          profileDisplayName: userProfile.displayName,
          profileAvatarUrl: userProfile.avatarUrl,
          profileCity: userProfile.city,
          profileState: userProfile.state,
          profileCountry: userProfile.country,
          profileInstagramUsername: userProfile.instagramUsername,
        } as EventParticipantDetails;

        queryClient.setQueryData<EventParticipantDetails[]>(
          eventParticipantKeys.byEvent(eventId),
          (old) => {
            if (!old) return [optimisticParticipant];
            // Update existing participant or add new one
            const existingIndex = old.findIndex((p) => p.userId === userId);
            if (existingIndex >= 0) {
              const updated = [...old];
              updated[existingIndex] = optimisticParticipant;
              return updated;
            }
            return [...old, optimisticParticipant];
          }
        );
      }

      return { previousParticipants };
    },
    onError: (_error, variables, context) => {
      if (context?.previousParticipants) {
        queryClient.setQueryData(
          eventParticipantKeys.byEvent(variables.eventId),
          context.previousParticipants
        );
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: eventParticipantKeys.byEvent(variables.eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventCouponKeys.byEvent(variables.eventId, variables.userId),
      });
    },
  });
}

export function useUserEvents(userId: string) {
  return useQuery({
    queryKey: eventParticipantKeys.byUser(userId),
    queryFn: () => fetchUserEvents(userId),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useLeaveEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: leaveEvent,
    onMutate: async ({ eventId, userId }) => {
      await queryClient.cancelQueries({
        queryKey: eventParticipantKeys.byEvent(eventId),
      });

      const previousParticipants = queryClient.getQueryData<
        EventParticipantDetails[]
      >(eventParticipantKeys.byEvent(eventId));

      // Optimistically remove the user from participants
      queryClient.setQueryData<EventParticipantDetails[]>(
        eventParticipantKeys.byEvent(eventId),
        (old) => {
          if (!old) return [];
          return old.filter((p) => p.userId !== userId);
        }
      );

      return { previousParticipants };
    },
    onError: (_error, variables, context) => {
      if (context?.previousParticipants) {
        queryClient.setQueryData(
          eventParticipantKeys.byEvent(variables.eventId),
          context.previousParticipants
        );
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: eventParticipantKeys.byEvent(variables.eventId),
      });
      queryClient.removeQueries({
        queryKey: eventCouponKeys.byEvent(variables.eventId, variables.userId),
      });
    },
  });
}
